import LocaleResolver from "csi/config/LocaleResolver";
import locale from "csi/locale";
import sessionConstants from "csi/sessionConstants";
import { C8AttributeId, C8Node, C8NodeType } from "csi/typings";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { ILocaleComponentResources, ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";

/**
 * Implement locale provider for given resolver
 */

export function getConfigLocaleProvider(resolver: LocaleResolver): ILocaleProvider {
	function actionResource(nodeType: C8NodeType, actionId?: string): string {
		return resolver.getTypeAction(nodeType, actionId);
	}

	function attributeResource(nodeType: C8NodeType, id?: C8AttributeId): string {
		return resolver.getTypeAttribute(nodeType, id);
	}

	function enumResource(enumValue: string, uppercase = false): string {
		return resolver.getEnumName(enumValue, uppercase);
	}

	function numberOfType(n: number, nodeType: string): string {
		return resource("ConjunctionNumberOfType", [n, type(nodeType, n !== 1)]);
	}

	function getAttributeType(type: string): string {
		return resolver.getAttributeType(type);
	}

	function getDatePattern(): string {
		return sessionConstants.datePattern;
	}

	function getErrorMessage(error: Error): string {
		return resolver.getErrorMessage(error);
	}

	function getTimePattern(): string {
		return sessionConstants.timePattern;
	}

	function getNameOrUnnamed(name: string): string {
		return name || resource(resources.RES_UNNAMED);
	}

	function getNodeName(node: C8Node): string {
		throw new Error("Not implemented");
	}

	function pathResource(nodeType: C8NodeType, path?: string): string {
		return resolver.getTypePath(nodeType, path);
	}

	function resource(res: string, resParams?: Array<number | string>): string {
		return res ? resolver.resolveResource(res, resParams) : "";
	}

	function type(nodeType: string, plural = false, lower = false): string {
		return plural ? resolver.getTypePlural(nodeType, lower) : resolver.getTypeName(nodeType, lower);
	}

	function viewResource(nodeType: C8NodeType, viewId?: string): string {
		return resolver.getTypeView(nodeType, viewId);
	}

	function getComponentItemName(item: ComponentItem): string {
		const { id, name, node, res, resParams } = item;

		if (!isNullOrUndefined(name)) {
			return name;
		} else if (res) {
			return resource(res, resParams);
		} else if (node) {
			return getNodeName(node);
		}

		return id ?? "";
	}

	function getComponentItemSlimName(item: ComponentItem): string {
		const name = getComponentItemName(item);
		const { name2 } = item;
		return name2 ? `${name} | ${name2}` : name;
	}

	const resources: ILocaleComponentResources = {
		RES_NEW_OF_TYPE: "New_Name",
		RES_UNNAMED: "NAME_EMPTY",
	};

	return {
		resources,
		actionResource,
		attributeResource,
		enumResource,
		getAttributeType,
		getComponentItemName,
		getComponentItemSlimName,
		getDatePattern,
		getErrorMessage,
		getTimePattern,
		getNameOrUnnamed,
		getNodeName,
		numberOfType,
		pathResource,
		resource,
		type,
		viewResource,
	};
}
